<template>
  <div id="top" class="portfolio-page">
    <div class="ticker" aria-label="Areas of expertise">
      <div class="ticker-track">
        <span>PRODUCT ENGINEERING</span><i>✳</i><span>QUALITY AUTOMATION</span><i>✳</i><span>WEB APPLICATIONS</span><i>✳</i><span>GAME DEVELOPMENT</span><i>✳</i>
        <span aria-hidden="true">PRODUCT ENGINEERING</span><i aria-hidden="true">✳</i><span aria-hidden="true">QUALITY AUTOMATION</span><i aria-hidden="true">✳</i><span aria-hidden="true">WEB APPLICATIONS</span><i aria-hidden="true">✳</i><span aria-hidden="true">GAME DEVELOPMENT</span><i aria-hidden="true">✳</i>
      </div>
    </div>

    <section class="hero page-shell" aria-labelledby="hero-title">
      <div class="hero-copy">
        <p class="section-code">INDEX / 00 — PORTFOLIO 2026</p>
        <h1 id="hero-title">
          I build software<br>
          that <em>earns</em> its<br>
          place on screen.
        </h1>
        <div class="hero-intro">
          <p>
            I’m <strong>Erdem Karataş</strong> — a computer engineer and product enthusiast - working
            across product development, quality automation and independent digital products.
          </p>
          <a class="text-link" href="#work">See selected work <span>↓</span></a>
        </div>
      </div>

      <aside class="hero-console" aria-label="Current status">
        <div class="console-bar">
          <span>ERDEM_OS.EXE</span>
          <span class="window-controls" aria-hidden="true">— □ ×</span>
        </div>
        <div class="console-body">
          <div class="console-orbit" aria-hidden="true">
            <span class="orbit orbit-one"></span>
            <span class="orbit orbit-two"></span>
            <span class="console-core">EK</span>
            <span class="satellite satellite-a"></span>
            <span class="satellite satellite-b"></span>
          </div>
          <dl class="console-stats">
            <div><dt>STATUS</dt><dd><span class="status-dot"></span> BUILDING</dd></div>
            <div><dt>NOW</dt><dd>QA & AUTOMATION</dd></div>
            <div><dt>LOCAL TIME</dt><dd>{{ localTime }}</dd></div>
          </dl>
        </div>
        <div class="console-note">Independent maker / engineer / perennial tinkerer</div>
      </aside>
    </section>

    <div class="statement-band">
      <p>I LIKE <strong>USEFUL SYSTEMS,</strong> CLEAR THINKING &amp; <em>SOFTWARE WITH A POINT OF VIEW.</em></p>
    </div>

    <section id="work" class="work-section page-shell" aria-labelledby="work-title">
      <header class="section-heading">
        <p class="section-code">INDEX / 01</p>
        <h2 id="work-title">Selected work</h2>
        <p>Three independent releases that made me earn exactly <strong>0$</strong> in total.</p>
      </header>

      <div class="featured-list">
        <article v-for="(project, index) in featuredProjects" :key="project.name" :class="['featured-project', `project-${project.slug}`]">
          <div class="project-visual" aria-hidden="true">
            <div v-if="project.slug === 'piano'" class="piano-art">
              <div class="mock-window">
                <div class="mock-bar"><i></i><i></i><i></i><span>LEARN PIANO STUDIO</span></div>
                <div class="music-staff"><b>♪</b><i></i><i></i><i></i><i></i><i></i><span class="note n1">●</span><span class="note n2">●</span><span class="note n3">●</span></div>
                <div class="lesson-progress"><span>LESSON 04</span><b><i></i></b><span>72%</span></div>
                <div class="piano-keys"><i v-for="key in 10" :key="key" :class="{ black: [2, 4, 7, 9].includes(key) }"></i></div>
              </div>
              <span class="art-sticker">C MAJOR<br>NO FEAR!</span>
            </div>

            <div v-else-if="project.slug === 'baller'" class="baller-art">
              <div class="score-card"><small>MODEL CONFIDENCE</small><strong>78<sup>%</sup></strong><span>HOME WIN</span></div>
              <div class="court">
                <span class="court-circle"></span><span class="court-line"></span>
                <i class="player p1"></i><i class="player p2"></i><i class="player p3"></i><i class="player p4"></i>
                <svg viewBox="0 0 300 180" role="presentation"><polyline points="20,145 62,108 104,121 148,62 191,81 232,32 280,46" /></svg>
              </div>
              <div class="baller-label">DATA IN.<br><strong>GAME ON.</strong></div>
            </div>

            <div v-else class="scrap-art">
              <div class="game-window">
                <div class="game-top"><span>SCRAP_SHIFT // WEB BUILD</span><b>HI: 00840</b></div>
                <div class="game-sky"><i class="pixel-cloud c1"></i><i class="pixel-cloud c2"></i><b class="game-title">SCRAP<br>SHIFT</b></div>
                <div class="scrap-ground">
                  <span class="scrap scrap-one"></span><span class="scrap scrap-two"></span><span class="scrap scrap-three"></span>
                  <span class="player-sprite">▰</span>
                </div>
                <div class="game-cta">[ PRESS START ]</div>
              </div>
            </div>
          </div>

          <div class="project-copy">
            <div class="project-count">0{{ index + 1 }} / 03</div>
            <p class="project-type">{{ project.type }}</p>
            <h3>{{ project.name }}</h3>
            <p class="project-description">{{ project.description }}</p>
            <ul class="tag-list" :aria-label="`${project.name} disciplines`">
              <li v-for="tag in project.tags" :key="tag">{{ tag }}</li>
            </ul>
            <a class="project-link" :href="project.url" target="_blank" rel="noopener noreferrer">
              {{ project.action }} <span>↗</span>
            </a>
          </div>
        </article>
      </div>
    </section>

    <section class="archive-section" aria-labelledby="archive-title">
      <div class="page-shell archive-inner">
        <header class="archive-header">
          <div>
            <p class="section-code inverse">ALSO SHIPPED</p>
            <h2 id="archive-title">Client &amp; product work</h2>
          </div>
          <p>Products I helped shape, build and move forward.</p>
        </header>
        <div class="archive-list">
          <a v-for="(item, index) in clientWork" :key="item.name" :href="item.url" target="_blank" rel="noopener noreferrer" class="archive-row">
            <span class="archive-number">{{ String(index + 1).padStart(2, '0') }}</span>
            <strong>{{ item.name }}</strong>
            <span>{{ item.role }}</span>
            <span class="archive-arrow">↗</span>
          </a>
        </div>
      </div>
    </section>

    <section id="lab" class="lab-section page-shell" aria-labelledby="lab-title">
      <header class="section-heading lab-heading">
        <p class="section-code">INDEX / 02 — ACADEMIC WORK</p>
        <h2 id="lab-title">The lab notebook</h2>
        <p>Computer vision, machine learning, networks and data — explored at Sabancı University.</p>
      </header>
      <div class="lab-grid">
        <article v-for="(project, index) in academicProjects" :key="project.title" class="lab-card">
          <div class="lab-card-top"><span>SU / {{ String(index + 1).padStart(2, '0') }}</span><i :class="`glyph glyph-${(index % 4) + 1}`"></i></div>
          <h3>{{ project.title }}</h3>
          <p>{{ project.description }}</p>
          <a v-if="project.url" :href="project.url" target="_blank" rel="noopener noreferrer">View repository ↗</a>
          <span v-else class="lab-discipline">{{ project.discipline }}</span>
        </article>
      </div>
    </section>

    <section id="experience" class="experience-section" aria-labelledby="experience-title">
      <div class="page-shell experience-inner">
        <header class="section-heading experience-heading">
          <p class="section-code">INDEX / 03</p>
          <h2 id="experience-title">Experience,<br><em>in practice.</em></h2>
          <p>From automated quality to product ownership — hands-on roles with real responsibility.</p>
        </header>

        <ol class="timeline">
          <li v-for="(role, index) in experience" :key="role.company" :class="{ current: index === 0 }">
            <div class="timeline-marker"><span></span></div>
            <div class="timeline-meta">
              <span>{{ role.period }}</span>
              <strong>{{ role.company }}</strong>
            </div>
            <div class="timeline-content">
              <h3>{{ role.role }}</h3>
              <p>{{ role.description }}</p>
            </div>
          </li>
        </ol>
      </div>
    </section>

    <section class="principles page-shell" aria-label="Working principles">
      <p class="section-code">WORKING NOTES / KEEP VISIBLE</p>
      <div class="principle-grid">
        <p><span>01</span>Make it useful before making it clever.</p>
        <p><span>02</span>Quality is a product feature, not a final step.</p>
        <p><span>03</span>Ship, observe, improve — then repeat.</p>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'LandingPage',
  data() {
    return {
      localTime: '',
      clockTimer: null,
      featuredProjects: [
        {
          slug: 'piano',
          name: 'Learn Piano Studio',
          type: 'Independent product / Music education',
          description: 'A browser-based piano learning application designed to make practice more structured, approachable and rewarding.',
          tags: ['Product', 'Web app', 'Music tech'],
          url: 'https://learnpianostudio.com',
          action: 'Visit the studio'
        },
        {
          slug: 'baller',
          name: 'BallerAlgo',
          type: 'Independent product / Sports analytics',
          description: 'An AI-driven sports prediction platform that turns match data into clearer, more useful insight for planning betting strategies.',
          tags: ['Founder', 'AI / ML', 'Full-stack'],
          url: 'https://balleralgo.com',
          action: 'Explore BallerAlgo'
        },
        {
          slug: 'scrap',
          name: 'Scrap Shift',
          type: 'Independent game / Browser',
          description: 'An original browser game conceived, built and released independently — no download, no setup, just press start and play.',
          tags: ['Game design', 'Development', 'Web'],
          url: 'https://erdemkaratas.itch.io/scrap-shift',
          action: 'Play on itch.io'
        }
      ],
      clientWork: [
        { name: 'Semosis', role: 'Product Manager', url: 'https://semosis.com' },
        { name: 'Toni Co.', role: 'Mobile Developer & Product Owner', url: 'https://apps.apple.com/tr/app/toni-co/id6456038882' },
        { name: 'OCN Gübre', role: 'Freelance Full-stack Developer', url: 'https://ocngubre.com' }
      ],
      academicProjects: [
        {
          title: 'Structural Damage Assessment Using Deep Learning',
          description: 'ResNet-18 and VGG16 architectures used to identify structural vulnerabilities from wall imagery.',
          discipline: 'DEEP LEARNING / COMPUTER VISION'
        },
        {
          title: 'Lake Water Quality Mapping from Satellite Images',
          description: 'A computer vision model for assessing lake water quality from satellite imagery to support conservation work.',
          discipline: 'COMPUTER VISION / REMOTE SENSING'
        },
        {
          title: 'Simple Message Networking via Channels',
          description: 'Client and server applications for message networking via channels, sockets and the TCP/IP protocol suite.',
          discipline: 'NETWORKS / DISTRIBUTED SYSTEMS'
        },
        {
          title: 'Classification of Bot Accounts & Political Posts',
          description: 'Machine learning and neural network models trained on manually annotated Twitter datasets.',
          discipline: 'MACHINE LEARNING / NLP'
        },
        {
          title: 'COVID-19 Vaccination Data & Election Results',
          description: 'An investigation into the relationship between vaccination rates and political preference in the United States.',
          discipline: 'DATA SCIENCE / VISUALISATION',
          url: 'https://github.com/erdemkaratash/vaxpol'
        }
      ],
      experience: [
        {
          period: 'CURRENT / FREELANCE',
          company: 'Lidion Bank Plc.',
          role: 'Quality Assurance & Automation Engineer',
          description: 'Providing hands-on quality assurance and automation engineering for banking software, with a focus on dependable releases and repeatable testing.'
        },
        {
          period: 'PREVIOUS ENGAGEMENT',
          company: 'Semosis',
          role: 'Product Manager',
          description: 'Led product thinking for a digital collection system bringing promissory-note payments into a modern online workflow.'
        },
        {
          period: 'PREVIOUS ENGAGEMENT',
          company: 'Toni Co.',
          role: 'Mobile Developer & Product Owner',
          description: 'Worked across mobile development and product ownership for a social platform built around sharing places and experiences.'
        },
        {
          period: 'FREELANCE PROJECT',
          company: 'OCN Gübre',
          role: 'Full-stack Developer',
          description: 'Designed and delivered a promotional web presence for an agricultural fertilizer brand.'
        }
      ]
    };
  },
  mounted() {
    this.updateClock();
    this.clockTimer = window.setInterval(this.updateClock, 30000);
  },
  beforeUnmount() {
    window.clearInterval(this.clockTimer);
  },
  methods: {
    updateClock() {
      this.localTime = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/Istanbul',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).format(new Date()) + ' / IST';
    }
  }
};
</script>

<style scoped>
.portfolio-page { position: relative; }
.page-shell { width: var(--page); margin: 0 auto; }
.section-code {
  margin: 0;
  font-family: "Courier New", monospace;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}
.section-code::before { content: '●'; margin-right: 8px; color: var(--red); }
.section-code.inverse::before { color: var(--yellow); }

.ticker {
  overflow: hidden;
  border-bottom: var(--line);
  background: var(--yellow);
  font-family: "Courier New", monospace;
  font-size: 0.73rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  white-space: nowrap;
}
.ticker-track {
  display: flex;
  width: max-content;
  padding: 8px 0;
  animation: ticker 30s linear infinite;
}
.ticker span { margin: 0 30px; }
.ticker i { font-style: normal; }
@keyframes ticker { to { transform: translateX(-50%); } }

.hero {
  display: grid;
  grid-template-columns: minmax(0, 1.55fr) minmax(330px, 0.68fr);
  gap: clamp(40px, 7vw, 110px);
  align-items: center;
  min-height: min(770px, calc(100vh - 110px));
  padding-top: 68px;
  padding-bottom: 70px;
}
.hero h1 {
  margin: 30px 0 46px;
  font-family: Georgia, "Times New Roman", serif;
  font-size: clamp(4rem, 8.8vw, 8.8rem);
  font-weight: 400;
  letter-spacing: -0.072em;
  line-height: 0.78;
}
.hero h1 em { position: relative; z-index: 1; font-weight: 700; }
.hero h1 em::after {
  position: absolute;
  z-index: -1;
  right: -0.03em;
  bottom: 0.03em;
  left: -0.03em;
  height: 0.17em;
  background: var(--yellow);
  content: '';
  transform: rotate(-1.5deg);
}
.hero-intro {
  display: grid;
  grid-template-columns: minmax(0, 520px) auto;
  gap: 36px;
  align-items: end;
}
.hero-intro p { margin: 0; font-size: clamp(1rem, 1.35vw, 1.18rem); }
.text-link {
  padding-bottom: 4px;
  border-bottom: 2px solid var(--ink);
  font-family: "Courier New", monospace;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-decoration: none;
  text-transform: uppercase;
  white-space: nowrap;
}
.text-link span { display: inline-block; transition: transform 150ms ease; }
.text-link:hover span { transform: translateY(4px); }

.hero-console { border: var(--line); background: var(--paper-light); box-shadow: 10px 10px 0 var(--ink); }
.console-bar {
  display: flex;
  justify-content: space-between;
  padding: 8px 10px;
  border-bottom: var(--line);
  background: var(--blue);
  color: #fff;
  font-family: "Courier New", monospace;
  font-size: 0.72rem;
  font-weight: 700;
}
.window-controls { letter-spacing: 0.35em; }
.console-body { padding: clamp(22px, 3vw, 42px); }
.console-orbit { position: relative; height: 260px; margin-bottom: 34px; }
.console-core {
  position: absolute;
  top: 50%; left: 50%;
  display: grid;
  width: 94px; height: 94px;
  place-items: center;
  border: var(--line);
  border-radius: 50%;
  background: var(--red);
  color: #fff;
  font-family: Georgia, serif;
  font-size: 2.4rem;
  font-style: italic;
  font-weight: 700;
  transform: translate(-50%, -50%);
}
.orbit { position: absolute; top: 50%; left: 50%; border: 1px solid var(--ink); border-radius: 50%; transform: translate(-50%, -50%); }
.orbit-one { width: 220px; height: 120px; transform: translate(-50%, -50%) rotate(25deg); }
.orbit-two { width: 140px; height: 240px; transform: translate(-50%, -50%) rotate(50deg); }
.satellite { position: absolute; width: 18px; height: 18px; border: var(--line); border-radius: 50%; background: var(--yellow); }
.satellite-a { top: 40px; right: 31px; }
.satellite-b { bottom: 28px; left: 42px; background: var(--blue); }
.console-stats { margin: 0; border-top: 1px solid var(--ink); }
.console-stats div { display: grid; grid-template-columns: 0.8fr 1fr; padding: 9px 0; border-bottom: 1px solid var(--ink); }
.console-stats dt, .console-stats dd { margin: 0; font-family: "Courier New", monospace; font-size: 0.7rem; }
.console-stats dt { color: #68665f; }
.console-stats dd { font-weight: 700; text-align: right; }
.status-dot { display: inline-block; width: 8px; height: 8px; margin-right: 6px; border-radius: 50%; background: #21a65d; animation: pulse 1.5s steps(2) infinite; }
@keyframes pulse { 50% { opacity: 0.3; } }
.console-note { padding: 10px; border-top: var(--line); background: var(--ink); color: #fff; font-family: "Courier New", monospace; font-size: 0.62rem; text-align: center; text-transform: uppercase; }

.statement-band { padding: 28px 24px; border-top: var(--line); border-bottom: var(--line); background: var(--red); color: #fff; text-align: center; transform: rotate(-0.6deg) scale(1.02); }
.statement-band p { margin: 0; font-family: "Courier New", monospace; font-size: clamp(0.78rem, 1.5vw, 1.1rem); letter-spacing: 0.04em; }
.statement-band em { font-family: Georgia, serif; font-size: 1.25em; }

.work-section { padding-top: 140px; padding-bottom: 140px; }
.section-heading { display: grid; grid-template-columns: 0.35fr 1fr 0.7fr; gap: 40px; align-items: start; margin-bottom: 76px; }
.section-heading h2 { margin: -12px 0 0; font-family: Georgia, "Times New Roman", serif; font-size: clamp(3rem, 6vw, 6.5rem); font-weight: 400; letter-spacing: -0.065em; line-height: 0.9; }
.section-heading > p:last-child { max-width: 390px; margin: 5px 0 0; font-size: 1.05rem; }
.featured-list { border-top: var(--line); }
.featured-project { display: grid; grid-template-columns: minmax(0, 1.15fr) minmax(330px, 0.85fr); min-height: 590px; border-bottom: var(--line); }
.featured-project:nth-child(even) { grid-template-columns: minmax(330px, 0.85fr) minmax(0, 1.15fr); }
.featured-project:nth-child(even) .project-visual { order: 2; border-right: 0; border-left: var(--line); }
.project-visual { position: relative; display: grid; min-height: 540px; overflow: hidden; place-items: center; border-right: var(--line); }
.project-copy { display: flex; flex-direction: column; align-items: flex-start; padding: clamp(38px, 5vw, 76px); }
.project-count { align-self: flex-end; margin-bottom: auto; font-family: "Courier New", monospace; font-size: 0.7rem; font-weight: 700; }
.project-type { margin: 45px 0 12px; font-family: "Courier New", monospace; font-size: 0.69rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; }
.project-copy h3 { margin: 0 0 24px; font-family: Georgia, "Times New Roman", serif; font-size: clamp(3rem, 5.5vw, 5.8rem); font-weight: 400; letter-spacing: -0.065em; line-height: 0.86; }
.project-description { max-width: 550px; margin: 0 0 28px; font-size: clamp(1rem, 1.25vw, 1.13rem); }
.tag-list { display: flex; flex-wrap: wrap; gap: 7px; margin: 0 0 36px; padding: 0; list-style: none; }
.tag-list li { padding: 5px 9px; border: 1px solid var(--ink); border-radius: 99px; font-family: "Courier New", monospace; font-size: 0.63rem; font-weight: 700; text-transform: uppercase; }
.project-link { display: flex; justify-content: space-between; width: 100%; margin-top: auto; padding: 13px 15px; border: var(--line); background: var(--ink); box-shadow: 5px 5px 0 var(--red); color: #fff; font-family: "Courier New", monospace; font-size: 0.75rem; font-weight: 700; letter-spacing: 0.06em; text-decoration: none; text-transform: uppercase; transition: 150ms ease; }
.project-link:hover { box-shadow: 2px 2px 0 var(--red); transform: translate(3px, 3px); }

.project-piano .project-visual { background: var(--blue); }
.piano-art { position: relative; width: min(82%, 650px); transform: rotate(-2deg); }
.mock-window { border: var(--line); background: var(--paper-light); box-shadow: 13px 13px 0 var(--ink); }
.mock-bar { display: flex; gap: 6px; align-items: center; padding: 8px; border-bottom: var(--line); font-family: "Courier New", monospace; font-size: 0.6rem; }
.mock-bar i { width: 9px; height: 9px; border: 1px solid var(--ink); border-radius: 50%; background: var(--red); }
.mock-bar i:nth-child(2) { background: var(--yellow); }
.mock-bar i:nth-child(3) { background: #70b885; }
.mock-bar span { margin-left: auto; }
.music-staff { position: relative; height: 150px; padding-top: 22px; overflow: hidden; }
.music-staff > i { display: block; height: 1px; margin: 14px 0; background: var(--ink); }
.music-staff > b { position: absolute; z-index: 1; top: 31px; left: 11%; font-family: Georgia, serif; font-size: 4.5rem; }
.music-staff .note { position: absolute; font-size: 1.2rem; }
.n1 { top: 44px; left: 43%; }.n2 { top: 73px; left: 61%; }.n3 { top: 29px; left: 79%; }
.lesson-progress { display: grid; grid-template-columns: auto 1fr auto; gap: 10px; align-items: center; padding: 12px; border-top: 1px solid var(--ink); font-family: "Courier New", monospace; font-size: 0.62rem; font-weight: 700; }
.lesson-progress b { height: 8px; border: 1px solid var(--ink); }
.lesson-progress b i { display: block; width: 72%; height: 100%; background: var(--red); }
.piano-keys { display: flex; height: 120px; border-top: var(--line); }
.piano-keys i { position: relative; flex: 1; border-right: 1px solid var(--ink); background: #fff; }
.piano-keys i:last-child { border-right: 0; }
.piano-keys i.black::after { position: absolute; z-index: 2; top: 0; left: -35%; width: 70%; height: 62%; border: 1px solid #fff; background: var(--ink); content: ''; }
.art-sticker { position: absolute; right: -40px; bottom: -35px; display: grid; width: 95px; height: 95px; place-items: center; border: var(--line); border-radius: 50%; background: var(--yellow); font-family: "Courier New", monospace; font-size: 0.66rem; font-weight: 700; text-align: center; transform: rotate(11deg); }

.project-baller .project-visual { background: #dfe1db; }
.baller-art { position: relative; width: 78%; height: 74%; border: var(--line); background: var(--green); box-shadow: 12px 12px 0 var(--ink); color: #fff; }
.court { position: absolute; inset: 12%; overflow: hidden; border: 2px solid rgba(255,255,255,.8); }
.court::before, .court::after { position: absolute; top: 0; bottom: 0; width: 25%; border: 1px solid rgba(255,255,255,.7); content: ''; }
.court::before { left: 0; }.court::after { right: 0; }
.court-circle { position: absolute; top: 50%; left: 50%; width: 80px; height: 80px; border: 1px solid rgba(255,255,255,.7); border-radius: 50%; transform: translate(-50%,-50%); }
.court-line { position: absolute; top: 0; bottom: 0; left: 50%; border-left: 1px solid rgba(255,255,255,.7); }
.court svg { position: absolute; inset: 0; width: 100%; height: 100%; }
.court polyline { fill: none; stroke: var(--yellow); stroke-width: 4; }
.player { position: absolute; z-index: 2; width: 14px; height: 14px; border: 2px solid var(--ink); border-radius: 50%; background: var(--red); }
.p1 { top: 22%; left: 16%; }.p2 { top: 64%; left: 35%; }.p3 { top: 30%; right: 23%; }.p4 { right: 8%; bottom: 12%; }
.score-card { position: absolute; z-index: 3; top: -30px; right: -35px; display: flex; flex-direction: column; width: 140px; padding: 13px; border: var(--line); background: var(--yellow); box-shadow: 5px 5px 0 var(--ink); color: var(--ink); font-family: "Courier New", monospace; transform: rotate(3deg); }
.score-card small { font-size: 0.52rem; font-weight: 700; }.score-card strong { font-size: 3rem; line-height: 1; }.score-card sup { font-size: 1rem; }.score-card span { font-size: 0.65rem; font-weight: 700; }
.baller-label { position: absolute; bottom: 25px; left: 25px; font-family: "Courier New", monospace; font-size: 1rem; letter-spacing: .06em; }

.project-scrap .project-visual { background: var(--yellow); }
.scrap-art { width: min(82%, 650px); transform: rotate(1.5deg); }
.game-window { overflow: hidden; border: var(--line); background: #89bcd1; box-shadow: 13px 13px 0 var(--ink); font-family: "Courier New", monospace; }
.game-top { display: flex; justify-content: space-between; padding: 8px 10px; border-bottom: var(--line); background: var(--ink); color: #fff; font-size: 0.58rem; }
.game-sky { position: relative; height: 210px; background: linear-gradient(#78b9d2 75%, #c8bd80 75%); }
.game-title { position: absolute; z-index: 2; top: 37px; left: 50%; color: var(--yellow); font-size: clamp(2.6rem, 5vw, 4.5rem); font-style: italic; letter-spacing: -0.11em; line-height: .72; text-align: center; text-shadow: 4px 4px 0 var(--ink); transform: translateX(-50%) rotate(-4deg); }
.pixel-cloud { position: absolute; width: 70px; height: 17px; background: rgba(255,255,255,.8); box-shadow: 16px -12px 0 rgba(255,255,255,.8), 34px 0 0 rgba(255,255,255,.8); }
.c1 { top: 35px; left: 22px; }.c2 { top: 77px; right: 55px; transform: scale(.7); }
.scrap-ground { position: relative; height: 100px; border-top: var(--line); background: #66594b; }
.scrap { position: absolute; bottom: 0; display: block; background: var(--red); border: 2px solid var(--ink); }
.scrap-one { left: 5%; width: 80px; height: 45px; transform: rotate(8deg); }.scrap-two { left: 31%; width: 50px; height: 75px; background: #777; transform: rotate(-12deg); }.scrap-three { right: 4%; width: 120px; height: 55px; background: var(--blue); clip-path: polygon(0 40%,25% 40%,25% 0,55% 0,55% 30%,100% 30%,100% 100%,0 100%); }
.player-sprite { position: absolute; z-index: 3; right: 35%; bottom: 18px; color: var(--yellow); font-size: 2.8rem; text-shadow: 3px 3px 0 var(--ink); }
.game-cta { padding: 10px; border-top: var(--line); background: var(--paper-light); font-size: 0.65rem; font-weight: 700; text-align: center; animation: pulse 1.2s steps(2) infinite; }

.archive-section { padding: 110px 0; border-top: var(--line); border-bottom: var(--line); background: var(--ink); color: #fff; }
.archive-header { display: grid; grid-template-columns: 1fr .5fr; gap: 60px; align-items: end; margin-bottom: 70px; }
.archive-header h2 { margin: 16px 0 0; font-family: Georgia, serif; font-size: clamp(3rem, 6vw, 6.3rem); font-weight: 400; letter-spacing: -.065em; line-height: .88; }
.archive-header > p { max-width: 350px; margin: 0; color: #cfcec8; }
.archive-list { border-top: 1px solid #fff; }
.archive-row { display: grid; grid-template-columns: 60px 1fr .8fr 30px; gap: 20px; align-items: center; padding: 24px 3px; border-bottom: 1px solid #777; text-decoration: none; transition: padding 160ms ease, background 160ms ease; }
.archive-row:hover { padding-right: 14px; padding-left: 14px; background: var(--blue); }
.archive-number, .archive-row > span:nth-child(3) { font-family: "Courier New", monospace; font-size: .68rem; text-transform: uppercase; }
.archive-row strong { font-family: Georgia, serif; font-size: clamp(1.5rem, 2.7vw, 2.6rem); font-weight: 400; }
.archive-arrow { font-size: 1.4rem; }

.lab-section { padding-top: 140px; padding-bottom: 140px; }
.lab-heading { margin-bottom: 70px; }
.lab-grid { display: grid; grid-template-columns: repeat(6, 1fr); border-top: var(--line); border-left: var(--line); }
.lab-card { display: flex; flex-direction: column; min-height: 370px; padding: 25px; border-right: var(--line); border-bottom: var(--line); background: var(--paper-light); }
.lab-card:nth-child(1), .lab-card:nth-child(2) { grid-column: span 3; }
.lab-card:nth-child(n+3) { grid-column: span 2; }
.lab-card-top { display: flex; justify-content: space-between; align-items: flex-start; min-height: 70px; font-family: "Courier New", monospace; font-size: .68rem; font-weight: 700; }
.glyph { display: block; width: 42px; height: 42px; border: var(--line); }
.glyph-1 { border-radius: 50%; background: var(--red); box-shadow: inset 0 0 0 8px var(--paper-light); }
.glyph-2 { background: repeating-linear-gradient(45deg, var(--blue) 0 5px, transparent 5px 10px); }
.glyph-3 { background: var(--yellow); transform: rotate(45deg) scale(.7); }
.glyph-4 { border-radius: 50% 0; background: var(--green); }
.lab-card h3 { max-width: 600px; margin: 22px 0 18px; font-family: Georgia, serif; font-size: clamp(1.6rem, 2.6vw, 2.55rem); font-weight: 400; letter-spacing: -.035em; line-height: 1.02; }
.lab-card p { max-width: 560px; margin: 0 0 30px; color: #55544f; }
.lab-discipline, .lab-card a { margin-top: auto; font-family: "Courier New", monospace; font-size: .63rem; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; }
.lab-card a { text-decoration-thickness: 1px; text-underline-offset: 4px; }

.experience-section { padding: 140px 0; border-top: var(--line); background: var(--yellow); }
.experience-inner { display: grid; grid-template-columns: .7fr 1fr; gap: clamp(60px, 10vw, 170px); align-items: start; }
.experience-heading { position: sticky; top: 130px; display: block; margin: 0; }
.experience-heading h2 { margin: 28px 0 34px; }
.experience-heading h2 em { font-weight: 700; }
.experience-heading > p:last-child { max-width: 410px; }
.timeline { margin: 0; padding: 0; list-style: none; }
.timeline li { position: relative; display: grid; grid-template-columns: 34px .48fr 1fr; gap: 22px; padding: 0 0 64px; }
.timeline li:last-child { padding-bottom: 0; }
.timeline-marker { position: relative; }
.timeline-marker::after { position: absolute; top: 19px; bottom: -64px; left: 8px; width: 2px; background: var(--ink); content: ''; }
.timeline li:last-child .timeline-marker::after { display: none; }
.timeline-marker span { position: relative; z-index: 1; display: block; width: 18px; height: 18px; border: var(--line); border-radius: 50%; background: var(--paper-light); }
.timeline .current .timeline-marker span { background: var(--red); }
.timeline-meta { display: flex; flex-direction: column; gap: 12px; }
.timeline-meta span { font-family: "Courier New", monospace; font-size: .62rem; font-weight: 700; }
.timeline-meta strong { font-size: 1rem; }
.timeline-content h3 { margin: -8px 0 16px; font-family: Georgia, serif; font-size: clamp(1.7rem, 3vw, 3.2rem); font-weight: 400; letter-spacing: -.045em; line-height: 1; }
.timeline-content p { margin: 0; }

.principles { padding-top: 95px; padding-bottom: 110px; }
.principle-grid { display: grid; grid-template-columns: repeat(3, 1fr); margin-top: 34px; border-top: var(--line); border-left: var(--line); }
.principle-grid p { min-height: 220px; margin: 0; padding: 28px; border-right: var(--line); border-bottom: var(--line); font-family: Georgia, serif; font-size: clamp(1.45rem, 2.5vw, 2.5rem); letter-spacing: -.035em; line-height: 1.05; }
.principle-grid span { display: block; margin-bottom: 60px; font-family: "Courier New", monospace; font-size: .65rem; font-weight: 700; }

@media (max-width: 1000px) {
  .hero { grid-template-columns: 1fr; }
  .hero-console { width: min(100%, 540px); margin-left: auto; }
  .section-heading { grid-template-columns: .35fr 1fr; }
  .section-heading > p:last-child { grid-column: 2; }
  .featured-project, .featured-project:nth-child(even) { grid-template-columns: 1fr; }
  .featured-project:nth-child(even) .project-visual { order: 0; border-left: 0; }
  .project-visual { border-right: 0; border-bottom: var(--line); }
  .lab-card:nth-child(n) { grid-column: span 3; }
  .experience-inner { grid-template-columns: 1fr; }
  .experience-heading { position: static; }
}

@media (max-width: 700px) {
  .hero { min-height: 0; padding-top: 52px; padding-bottom: 65px; }
  .hero h1 { margin: 28px 0 34px; font-size: clamp(3.65rem, 17vw, 6rem); }
  .hero-intro { grid-template-columns: 1fr; gap: 28px; }
  .text-link { justify-self: start; }
  .hero-console { width: calc(100% - 8px); margin-right: 8px; }
  .console-orbit { height: 220px; }
  .statement-band { padding: 21px 18px; }
  .work-section, .lab-section, .experience-section { padding-top: 88px; padding-bottom: 88px; }
  .section-heading { display: block; margin-bottom: 48px; }
  .section-heading h2 { margin: 22px 0 28px; }
  .featured-project { min-height: 0; }
  .project-visual { min-height: 400px; }
  .project-copy { min-height: 490px; padding: 30px 22px; }
  .project-count { margin-bottom: 30px; }
  .project-type { margin-top: 0; }
  .music-staff { height: 115px; }
  .piano-keys { height: 90px; }
  .art-sticker { right: -18px; width: 78px; height: 78px; }
  .baller-art { width: 82%; height: 66%; }
  .score-card { right: -20px; width: 120px; }
  .game-sky { height: 180px; }
  .archive-section { padding: 80px 0; }
  .archive-header { grid-template-columns: 1fr; gap: 24px; margin-bottom: 46px; }
  .archive-row { grid-template-columns: 36px 1fr 25px; gap: 12px; }
  .archive-row > span:nth-child(3) { grid-column: 2; }
  .archive-arrow { grid-column: 3; grid-row: 1 / span 2; }
  .lab-grid { grid-template-columns: 1fr; }
  .lab-card:nth-child(n) { grid-column: auto; min-height: 330px; }
  .experience-heading { margin-bottom: 55px; }
  .timeline li { grid-template-columns: 25px 1fr; gap: 14px; }
  .timeline-meta, .timeline-content { grid-column: 2; }
  .timeline-content h3 { margin-top: 5px; }
  .timeline-marker { grid-row: 1 / span 2; }
  .principles { padding-top: 70px; padding-bottom: 75px; }
  .principle-grid { grid-template-columns: 1fr; }
  .principle-grid p { min-height: 170px; }
  .principle-grid span { margin-bottom: 38px; }
}
</style>
